import { BrandTypes, FieldTypeAsset } from '@allesauto/utils/consts';
import { isAuregoShop } from '@allesauto/utils/funcs.ts';

import Button from '../button';
import NextImage from '../nextImage';
import { useWhiteLabel } from '../whiteLabel';

type Props = {
  headline?: string;
  subline?: string;
  buttonText?: string;
  handleButtonClick?: () => void;
  brandType: BrandTypes;
  imageDesktop: FieldTypeAsset;
  imageMobile: FieldTypeAsset;
};

const BrandHero = ({
  headline,
  subline,
  buttonText,
  handleButtonClick,
  brandType,
  imageDesktop,
  imageMobile,
}: Props) => {
  const isLargeImage = brandType === BrandTypes.largeImage;
  const aspectRatio = isLargeImage ? 'aspect-[2/1]' : 'aspect-square';
  const withSubline = !!subline ?? false;
  const withButton = !!buttonText ?? false;
  const whiteLabel = useWhiteLabel();

  const auregoButtonClassName =
    'monochrome:!bg-black monochrome:!border-grey90 monochrome:hover:!bg-grey90 monochrome:hover:!border-grey90 monochrome:hover:!text-white monochrome:!text-white';

  const classNames = {
    container: 'relative flex flex-col lg:flex-row-reverse',
    image: `relative w-full ${
      isLargeImage ? '' : 'lg:w-1/2'
    } aspect-square lg:${aspectRatio}`,
    visibleOnMobile: 'block lg:hidden',
    visibleOnDesktop: 'hidden lg:block',
    headlineContainer: `w-full lg:flex lg:flex-col lg:justify-between p-[calc(100%/24)] lg:p-85px bg-green60 monochrome:bg-grey20 ${
      isLargeImage
        ? 'lg:max-w-xl lg:w-auto lg:absolute lg:bottom-0 lg:left-0 lg:px-60px lg:py-40px'
        : 'lg:w-1/2'
    }`,
    headline: `font-headline text-headline1 text-purple80 monochrome:text-grey90`,
    subline: `lg:content-center text-p2 mt-40px text-grey100`,
    button: `w-full mt-40px ${
      isAuregoShop(whiteLabel) && auregoButtonClassName
    }`,
  };

  return (
    <div className={classNames.container}>
      <NextImage
        useCustomLoader={false}
        alt={imageMobile.alt}
        src={imageMobile.filename}
        className={`${classNames.image} ${classNames.visibleOnMobile}`}
        layout="fill"
        objectFit="cover"
        quality={50}
      />
      <NextImage
        useCustomLoader={false}
        alt={imageDesktop.alt}
        src={imageDesktop.filename}
        className={`${classNames.image} ${classNames.visibleOnDesktop}`}
        layout="fill"
        objectFit="cover"
        quality={75}
      />
      {headline && (
        <div className={classNames.headlineContainer}>
          <h2 className={classNames.headline}>{headline}</h2>
          {withSubline && <h3 className={classNames.subline}>{subline}</h3>}
          {withButton && (
            <Button
              darkBg
              onClick={handleButtonClick}
              text={buttonText}
              customClassName={classNames.button}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default BrandHero;
